import { useState } from 'react'
import { tokens } from '@/theme'
import { APP_ROLES, type AppRole } from '@/auth/roles'
import { applyDevRoles, DEV_ROLE_SWITCHER, readDevRoles } from '@/auth/devRoles'
import { useRoles } from '@/auth/useRoles'

/**
 * Dev only: pick the roles the app and the API treat you as having
 * (adr/012). Renders nothing unless the bundle was built with the switcher.
 * Applying reloads the page.
 */
export function DevRolePicker() {
  const { raw } = useRoles()
  const picked = readDevRoles()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<AppRole[]>(raw)

  if (!DEV_ROLE_SWITCHER) return null

  const toggle = (role: AppRole) =>
    setDraft(d => (d.includes(role) ? d.filter(r => r !== role) : [...d, role]))

  return (
    <div
      role="region"
      aria-label="Dev roles"
      style={{
        position: 'fixed',
        right: 16,
        // Clear of the phone tab bar.
        bottom: 84,
        zIndex: 120,
        background: tokens.inkPanel,
        color: tokens.inkPanelText3,
        border: `1px solid ${tokens.inkPanelLine}`,
        borderRadius: tokens.radiusMd,
        boxShadow: tokens.shadowDropdown,
        fontFamily: tokens.fontDisplay,
        fontSize: 12,
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setDraft(raw)
          setOpen(o => !o)
        }}
        style={{
          background: 'transparent',
          border: 'none',
          color: picked ? tokens.goldSoft : tokens.inkPanelText,
          padding: '8px 12px',
          cursor: 'pointer',
          font: 'inherit',
          fontWeight: tokens.weightDemi,
        }}
      >
        Dev roles: {picked ? picked.join(', ') : 'from token'}
      </button>
      {open && (
        <div style={{ display: 'grid', gap: 6, padding: '4px 12px 12px' }}>
          {APP_ROLES.map(role => (
            <label key={role} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={draft.includes(role)} onChange={() => toggle(role)} />
              {role}
            </label>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button
              type="button"
              disabled={draft.length === 0}
              onClick={() => applyDevRoles(draft)}
              style={pickerButton}
            >
              Apply
            </button>
            {picked && (
              <button type="button" onClick={() => applyDevRoles(null)} style={pickerButton}>
                Use my token
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const pickerButton = {
  background: tokens.inkPanel2,
  color: tokens.paper,
  border: `1px solid ${tokens.inkPanelLine}`,
  borderRadius: tokens.radiusMd,
  padding: '5px 10px',
  font: 'inherit',
  cursor: 'pointer',
} as const
